import { SPRITESHEET_UI, TWEEN_ONUPDATE } from "Data/Constants";
import { GameConfig } from "Data/GameConfig";
import { TimelineLite } from "gsap/TimelineLite";
import { TweenLite } from "gsap/TweenLite";

import * as HLF from "html-living-framework";
import { PointUtils } from "html-living-framework/lib/Utils";
import { makeSpine, makeSprite } from "html-living-framework/lib/Utils/PixiUtils";
import { floatBetween } from "html-living-framework/lib/Utils/Random";
import type { ILevelState } from "Interfaces/ILevelState";

// import "vendor/pixi-spine-debug";

export abstract class InteriorLuna extends PIXI.Container {
    protected spineContainer = this.addChild(new PIXI.Container());
    protected spine: PIXI.spine.Spine;
    protected bg: PIXI.Sprite;
    protected abstract left: number;
    protected abstract bottom: number;
    protected _subscriptions: any[] = [];
    protected isTalking: boolean = false;
    protected queuedBlink?: TimelineLite;
    private readonly hatPositionOffset = new PIXI.Point(75, 20);

    public constructor() {
        super();
        const scale = 0.635; // eyeballed
        this.spineContainer.scale.set(scale);

        this.bg = this.spineContainer.addChild(makeSprite("container", SPRITESHEET_UI));
        this.bg.anchor.set(0.5, 1);
        this.bg.scale.set(1 / scale);
        this.spine = this.spineContainer.addChild(makeSpine("luna"));
        this.spine.position.x = 28; // eyeballed
        this.spine.position.y = 45; // eyeballed
        // @ts-ignore
        // this.spine.drawDebug = true;

        this.spine.state.addListener({
            complete: this.onAnimComplete.bind(this),
        });

        this._subscriptions.push(
            PubSub.subscribe(HLF.VOICEOVER_STARTED, this.StartTalking.bind(this)),
            PubSub.subscribe(HLF.VOICEOVER_ENDED, this.StopTalking.bind(this)),
        );
    }

    public init(pSize: PIXI.Point, pData: ILevelState) {
        const id = pData.id;
        const config = GameConfig.locations[id];

        this.spine.scale.set(0.5);
        this.spineContainer.position.set(
            -(1024 / 2) + this.left,
             (768 / 2) - this.bottom,
        );
        if (this.spine.state.hasAnimation("idle")) {
            this.playAnimation("idle", true);
        }
        else {
            const track = this.spine.state.setAnimation(0, "blink", false);
            track.trackTime = track.animationEnd;
            this.spine.update(0);
        }
        return this;
    }

    public animateIn() {
        let now: number = PIXI.ticker.shared.lastTime;
        let lastTick: number = 0;
        let deltaTime: number = 0;
        TweenLite
            .from(this, 0.5, {pixi: {positionY: 40 }})
            .eventCallback(TWEEN_ONUPDATE, () => {
                lastTick = now;
                now = PIXI.ticker.shared.lastTime;
                deltaTime = (now - lastTick) / 1000;
                this.update(deltaTime);
            })
        ;
    }

    public onResize(pSize: PIXI.Point) {
        //
    }

    public update(pDeltaTime: number): void {
        if (this.spine !== undefined) {
            this.spine.update(pDeltaTime);
        }
    }

    public destroy(pOptions?: PIXI.DestroyOptions | boolean) {
        this._subscriptions.forEach(PubSub.unsubscribe);
        TweenLite.killTweensOf(this);
        this.queuedBlink?.kill();
        super.destroy(pOptions);
    }

    public getHatPosition(): PIXI.Point {
        const bone = this.spine.skeleton.findBone("hat");
        const x = (bone.worldX + this.spine.position.x) * this.spine.scale.x;
        const y = (bone.worldY + this.spine.position.y) * this.spine.scale.y;
        return this.spineContainer.toGlobal(PointUtils.add(new PIXI.Point(x, y), this.hatPositionOffset));
    }

    protected StartTalking() {
        const wasTalking = this.isTalking;
        if (!wasTalking) {
            // console.log("begin lip flap");
        }
        else {
            // console.log("already flapping");
            return;
        }
        this.isTalking = true;
        this.spine.state.setAnimation(1, "talking", true);

        let track = this.spine.state.tracks[0];
        if (!track?.animation.name.endsWith("-talk")) {
            const newAnim = track.animation.name + "-talk";
            if (this.spine.state.hasAnimation(newAnim)) {
                // console.log(newAnim);
                this.spine.state.setAnimation(0, newAnim, track.loop).trackTime = track.trackTime;
            }
            else {
                track = this.spine.state.setAnimation(0, "blink", false);
                track.trackTime = track.animationEnd;
                // console.log("paused at end of blink animation");
            }
        }
    }

    protected StopTalking() {
        const wasTalking = this.isTalking;
        if (wasTalking) {
            // console.log("end lip flap");
        }
        else {
            // console.log("wasn't flapping");
            return;
        }
        this.isTalking = false;
        this.spine.state.setEmptyAnimation(1, 0);
    }

    protected PlayIdleAnimation() {
        if (this.spine.state.hasAnimation("idle")) {
            this.playAnimation("idle", true);
        }
        this.queuedBlink?.kill();
        this.queuedBlink = new TimelineLite();
        this.queuedBlink
            .call(this.PlayBlinkAnimation.bind(this))
            .delay(floatBetween(1, 2))
        ;
    }

    protected playAnimation(pAnimation: string, loop: boolean = false): void {
        this.queuedBlink?.kill();
        if (this.isTalking) {
            if (this.spine.state.hasAnimation(pAnimation + "-talk")) {
                pAnimation += "-talk";
            }
        }
        // console.log(pAnimation);
        this.spine.state.setAnimation(0, pAnimation, loop);
    }

    protected PlayBlinkAnimation() {
        this.playAnimation("blink", false);
    }

    private onAnimComplete(pTrack: PIXI.spine.core.TrackEntry): void {
        // anim complete after VO
        if (pTrack.trackIndex === 0) {
            switch (pTrack.animation.name) {
                case "instructs-talk":
                case "idle-hat-on-talk":
                case "blink":
                    this.PlayIdleAnimation();
                    break;
            }
        }
        // VO complete while anim is holding on a frame
        else if (pTrack.trackIndex === 1) {
            if (!this.isTalking && this.spine.state.tracks[0].isComplete()) {
                switch (this.spine.state.tracks[0].animation.name) {
                    case "idle-hat-on-talk":
                    case "instructs-talk":
                        this.PlayIdleAnimation();
                        break;
                }
            }
        }
    }
}
